import { produce } from 'immer';
import * as R from 'rambda';
import type { StateSnapshot } from 'react-virtuoso';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { ConversationFormDataType } from '@/components/conversations/conversation-form';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import type { ConversationMessageTypeUnion } from '@/models/conversations/conversation';
import type {
  ConversationGroupType,
  ConversationSessionsTypeUnion,
  ConversationSessionType,
} from '@/models/conversations/conversation-session';
import ROUTES from '@/routes';
import { messageManager } from '@/services/message-manager';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import {
  addUnreadCount,
  getAllSessions,
  getSessionByKey,
  getSessions,
  updateSessionUnreadCount,
} from '@/utils/cache-session';
import { SESSIONS_STORAGE_KEY } from '@/utils/constants';
import { queryClient } from '@/utils/query-client';
import { generateSessionKey, parseSessionKey, type SessionKey } from '@/utils/session';
import { dbStore } from '@/utils/storage';

type SessionType = SessionKey extends `session-${infer _}` ? ConversationSessionType : ConversationGroupType;

export type DraftType = Pick<ConversationFormDataType, 'Content' | 'AtIDs' | 'Quote'> & { Timestamp: number };

// export type PositionSnapshotType = number;
export type PositionSnapshotType = (StateSnapshot & { anchorId?: number }) | number;

interface ConversationSessionState {
  hydrated: boolean;
  lastReadIds: Record<string, number>;
  lastReadNotices: Record<string, string>;
  lastPositions: Record<string, PositionSnapshotType>;
  lastUnReadSessionIds: Record<string, Record<number, number>>;
  hiddenSessions: string[];
  subscribedSessions: { userId: number }[];
  drafts: Record<string, DraftType>;
}

interface ConversationSessionActions {
  addSession(session: ConversationSessionsTypeUnion): void;

  removeSession(sessionKey: SessionKey): ConversationSessionsTypeUnion | undefined;

  removeSessionByUserId(userId: number): void;

  subscribeSessionByUserId(userId: number): void;

  unSubscribeSessionByUserId(userId: number): void;

  setLastRead(sessionKey: string, lastReadId?: number): Promise<void>;

  clearLastRead(sessionKey: string): void;

  setLastReadNotice(sessionKey: string, notice: string | null | undefined): void;

  addUnread(sessionKey: string, message?: ConversationMessageTypeUnion, updateCount?: number): void;

  clearUnreadCount(sessionKey: string, messageId?: number): void;

  updateSession(sessionKey: SessionKey, session: Partial<Omit<SessionType, 'ID' | 'UserID' | 'SessionType'>>): void;

  hideSession(sessionKey: string): void;

  unhideSession(sessionKey: string): void;

  hideAllCurrentSessions(): void;

  clear(): void;

  setHydrated(hasHydrated: boolean): void;

  updateDraft(sessionKey: string, draft: Omit<DraftType, 'Timestamp'>): void;

  deleteDraft(sessionKey: string): void;

  clearDrafts(): void;

  setLastPosition(sessionKey: string, position: PositionSnapshotType): void;

  clearLastPosition(sessionKey: string): void;

  clearLastPositions(options?: { keepMainListPosition: boolean }): void;

  setLastUnReadSessionId(sessionKey: string, userId: number, lastSessionId: number): void;
}

export const useConversationSessionStore = create<ConversationSessionState & ConversationSessionActions>()(
  persist(
    immer((set, get) => ({
      hydrated: false as boolean,
      lastReadIds: {} as Record<string, number>,
      lastReadNotices: {} as Record<string, string>,
      lastPositions: {} as Record<string, PositionSnapshotType>,
      lastUnReadSessionIds: {} as Record<string, Record<number, number>>,
      hiddenSessions: [] as string[],
      subscribedSessions: [] as { userId: number }[],
      drafts: {} as Record<string, DraftType>,

      setLastReadNotice(sessionKey, notice) {
        set((state) => {
          if (notice === null) {
            delete state.lastReadNotices[sessionKey];
          } else if (notice !== undefined) {
            state.lastReadNotices[sessionKey] = notice;
          }
        });
      },
      async setLastRead(sessionKey, lastReadId) {
        const { lastReadIds } = get();

        let messageId = lastReadId;
        if (messageId === undefined) {
          const [type, id] = parseSessionKey(sessionKey);
          if (!id) return;
          messageId = await messageManager.dispatch('getLastReadMessageId', type, id);
        }

        set((state) => {
          if ((lastReadIds[sessionKey] ?? 0) > messageId) return;

          state.lastReadIds[sessionKey] = messageId;
        });
      },
      clearLastRead(sessionKey) {
        set((state) => {
          state.lastReadIds[sessionKey] = 0;
        });
      },
      addUnread(sessionKey, message, updateCount = 1) {
        const [type, id] = parseSessionKey(sessionKey);
        if (!id) return;

        const currentMessagesKey = useMessageStore.getState().key;
        if (currentMessagesKey !== sessionKey) addUnreadCount(type, id, updateCount);

        if (!message || message.Status === null) return;

        queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            if (draft) {
              const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);

              const authId = useAuthStore.getState().auth?.UserID;
              if (session) {
                session.LastMsgID = message.ID;
                session.LastMsgType = message.MsgType;
                session.LastMsg =
                  session.SessionType === 'group' && message.UserID !== authId
                    ? `${message.UserNickName}：${message.Message}`
                    : message.Message;
                session.LastMsgTimeStamp = message.CreateTime;
                if (session.SessionType === 'group' && 'GroupID' in message) {
                  session.GotAt = message.GotAt ?? false;
                }
              }
            }
            return draft;
          }),
        );

        get().unhideSession(sessionKey);
      },
      clearUnreadCount(sessionKey, messageId) {
        const [type, id] = parseSessionKey(sessionKey);
        if (!id) return;

        const lastReadId = get().lastReadIds[sessionKey];

        if (messageId) {
          if (lastReadId === undefined) {
            updateSessionUnreadCount(type, id, 1);
          } else if (messageId > lastReadId) {
            updateSessionUnreadCount(type, id, (prev) => (prev > 0 ? prev - 1 : 0));
          }

          set((state) => {
            state.lastReadIds[sessionKey] = messageId;
          });
        } else {
          updateSessionUnreadCount(type, id, 0);
        }
      },

      addSession(session) {
        queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) => {
          if (!state) return [session];

          return state.concat([session]);
        });

        set((state) => {
          const key = generateSessionKey(session.SessionType, session.ID);
          state.lastReadIds[key] = 0;
        });
      },
      removeSession(sessionKey) {
        queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return;
            const sessionIndex = draft.findIndex((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
            if (sessionIndex >= 0) {
              draft.splice(sessionIndex, 1);
            }
          }),
        );

        set((state) => {
          state.hiddenSessions = R.without([sessionKey], state.hiddenSessions);
          delete state.lastReadIds[sessionKey];
          delete state.lastReadNotices[sessionKey];
          delete state.drafts[sessionKey];
        });

        return getSessionByKey(sessionKey);
      },
      removeSessionByUserId(userId) {
        const session = getSessions('session').find((s) => s.UserID === userId);

        if (session) {
          get().removeSession(generateSessionKey('session', session.ID));
        }
      },
      subscribeSessionByUserId(userId) {
        const isAlreadySubscribed = get().subscribedSessions.some(({ userId: id }) => id === userId);
        if (isAlreadySubscribed) return;

        set((state) => {
          state.subscribedSessions.push({
            userId,
          });
        });
      },
      unSubscribeSessionByUserId(userId) {
        set((state) => {
          state.subscribedSessions = state.subscribedSessions.filter((session) => session.userId !== userId);
        });
      },
      clear() {
        set((state) => {
          queryClient.removeQueries({
            queryKey: allChatSessionQueryOptions.queryKey,
          });
          state.hiddenSessions = [];
          state.lastReadIds = {};
          state.lastReadNotices = {};
          state.lastPositions = {};
          state.drafts = {};
        });
      },
      setHydrated(hasHydrated: boolean) {
        set((state) => {
          state.hydrated = hasHydrated;
        });
      },
      updateSession(sessionKey, partialSession) {
        queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
          produce(state, (draft) => {
            if (!draft) return;

            const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
            if (session) Object.assign(session, partialSession);
          }),
        );
      },
      hideSession(sessionKey: string) {
        set((state) => {
          queryClient.setQueryData(allChatSessionQueryOptions.queryKey, (state) =>
            produce(state, (draft) => {
              if (!draft) return;

              const session = draft.find((s) => generateSessionKey(s.SessionType, s.ID) === sessionKey);
              if (session) {
                session.UnReadCount = 0;
                session.UnReadMaxID = session.LastMsgID;
              }
            }),
          );
          state.hiddenSessions.push(sessionKey);
        });
      },
      hideAllCurrentSessions() {
        set((state) => {
          state.hiddenSessions = getAllSessions().map((s) => generateSessionKey(s.SessionType, s.ID));
        });
      },
      unhideSession(sessionKey: string) {
        set((state) => {
          state.hiddenSessions = state.hiddenSessions.filter((s) => s !== sessionKey);
        });
      },
      updateDraft(sessionKey, draft) {
        set((state) => {
          state.drafts[sessionKey] = {
            ...draft,
            Content: draft.Content?.trim(),
            Timestamp: Date.now(),
          };
        });
      },
      deleteDraft(sessionKey) {
        set((state) => {
          delete state.drafts[sessionKey];
        });
      },
      clearDrafts() {
        set((state) => {
          state.drafts = {};
        });
      },

      setLastPosition(sessionKey, position) {
        set((state) => {
          state.lastPositions[sessionKey] = position;
        });
      },
      clearLastPosition(sessionKey) {
        set((state) => {
          delete state.lastPositions[sessionKey];
        });
      },
      clearLastPositions(options) {
        set((state) => {
          const mainListPosition = {
            [ROUTES.MESSAGES.$path()]: state.lastPositions[ROUTES.MESSAGES.$path()] as PositionSnapshotType,
          };
          state.lastPositions = options?.keepMainListPosition ? mainListPosition : {};
        });
      },
      setLastUnReadSessionId(sessionKey, userId, lastSessionId) {
        set((state) => {
          if (!state.lastUnReadSessionIds[sessionKey]) {
            state.lastUnReadSessionIds[sessionKey] = {};
          }
          state.lastUnReadSessionIds[sessionKey][userId] = lastSessionId;
        });
      },
    })),
    {
      name: SESSIONS_STORAGE_KEY,
      version: 9,
      storage: dbStore,
      partialize: (state) => ({
        lastReadIds: state.lastReadIds,
        lastReadNotices: state.lastReadNotices,
        lastPositions: state.lastPositions,
        lastUnReadSessionIds: state.lastUnReadSessionIds,
        hiddenSessions: state.hiddenSessions,
        drafts: state.drafts,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
      migrate: () => ({
        lastReadIds: {},
        lastReadNotices: {},
        lastPositions: {},
        lastUnReadSessionIds: {},
        hiddenSessions: [] as string[],
        drafts: {},
      }),
    },
  ),
);

export function promisifySessionsHydration() {
  return new Promise<void>((resolve) => {
    if (useConversationSessionStore.getState().hydrated) {
      resolve();
    } else {
      const unsubscribe = useConversationSessionStore.subscribe(({ hydrated }) => {
        if (hydrated) {
          unsubscribe();
          resolve();
        }
      });
    }
  });
}

export const clearLastPosition = (key: string | undefined, options?: { keepMainListPosition: boolean }) => {
  if (key) {
    useConversationSessionStore.getState().clearLastPosition(key);
  } else {
    useConversationSessionStore.getState().clearLastPositions(options);
  }
};
